import { useState } from "react";
import { useTheme } from "../../context/ThemeContext";
import {
  GraduationCap,
  Palette,
  BookOpen,
  Users,
  MessagesSquare,
  Globe,
  Award,
  Tag,
} from "lucide-react";
import BrandingTab from "./learningPlatform/BrandingTab";
import CoursesTab from "./learningPlatform/CoursesTab";
import StudentsTab from "./learningPlatform/StudentsTab";
import CommunityTab from "./learningPlatform/CommunityTab";
import CertificatesTab from "./learningPlatform/CertificatesTab";
import PricingTab from "./learningPlatform/PricingTab";
import DomainTab from "./learningPlatform/DomainTab";

const TAB_GROUPS = [
  {
    label: "Instellingen",
    tabs: [
      { id: "branding", label: "Stijl", icon: Palette, Component: BrandingTab },
      { id: "pricing", label: "Prijzen", icon: Tag, Component: PricingTab },
      { id: "certificates", label: "Certificaten", icon: Award, Component: CertificatesTab },
      { id: "domain", label: "Domein", icon: Globe, Component: DomainTab },
    ],
  },
  {
    label: "Inhoud",
    tabs: [
      { id: "courses", label: "Cursussen", icon: BookOpen, Component: CoursesTab },
      { id: "students", label: "Studenten", icon: Users, Component: StudentsTab },
      { id: "community", label: "Community", icon: MessagesSquare, Component: CommunityTab },
    ],
  },
];

const TABS = TAB_GROUPS.flatMap((g) => g.tabs);

const LearningPlatformManager = () => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [activeTab, setActiveTab] = useState("branding");

  const ActiveComponent =
    TABS.find((t) => t.id === activeTab)?.Component ?? BrandingTab;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div
          className={`h-10 w-10 rounded-lg flex items-center justify-center ${
            isDark
              ? "bg-white/[0.06] text-[#4d7ee0]"
              : "bg-[#1E4FCF]/10 text-[#1E4FCF]"
          }`}
        >
          <GraduationCap size={20} />
        </div>
        <div>
          <h1
            className={`text-lg font-semibold ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            Leerplatform
          </h1>
          <p
            className={`text-[12px] ${
              isDark ? "text-gray-500" : "text-gray-500"
            }`}
          >
            Beheer je cursussen, studenten en community
          </p>
        </div>
      </div>

      <div className={`rounded-xl border p-3 space-y-2 ${isDark ? "border-white/[0.06] bg-white/[0.02]" : "border-gray-200 bg-gray-50/50"}`}>
        {TAB_GROUPS.map((group, gi) => (
          <div key={group.label}>
            {gi > 0 && (
              <div className={`h-px mb-2 ${isDark ? "bg-white/[0.06]" : "bg-gray-200"}`} />
            )}
            <div className="flex items-center gap-3">
              <span className={`text-[10px] font-semibold uppercase tracking-wider w-20 flex-shrink-0 ${isDark ? "text-gray-600" : "text-gray-400"}`}>
                {group.label}
              </span>
              <div className="flex flex-wrap gap-1">
                {group.tabs.map((tab) => {
                  const Icon = tab.icon;
                  const active = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium transition-all whitespace-nowrap border ${
                        active
                          ? isDark
                            ? "bg-white/[0.08] text-white border-white/[0.12]"
                            : "bg-white text-gray-900 border-gray-200 shadow-sm"
                          : isDark
                            ? "text-gray-500 hover:text-gray-200 border-transparent hover:bg-white/[0.04]"
                            : "text-gray-500 hover:text-gray-800 border-transparent hover:bg-white"
                      }`}
                    >
                      <Icon size={13} strokeWidth={active ? 2 : 1.6} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div
        className={`rounded-xl border p-6 ${
          isDark
            ? "bg-[#0f0f0f] border-white/[0.06]"
            : "bg-white border-gray-200/70"
        }`}
      >
        <ActiveComponent isDark={isDark} />
      </div>
    </div>
  );
};

export default LearningPlatformManager;
